import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import type { AgentPhone } from "@/types/agent/agent";
import type { HubContactPhone } from "@/types/agent/info-links";

/** Mirrors the server's sanitiser cap — what can't be served isn't offered. */
const MAX_LABEL = 60;

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-1 focus:ring-genie-primary sm:text-sm";

export interface ContactPhoneOption {
  phone: AgentPhone;
  kind: "Transfer number" | "Escalation number";
}

interface ContactPhonesPickerProps {
  genieId: string;
  /** The Genie's own line, published automatically while it is answering. */
  primaryNumber: string | null;
  /** The Genie's transfer and escalation numbers — the only ones that can be picked. */
  options: ContactPhoneOption[];
  value: HubContactPhone[];
  onChange: (phones: HubContactPhone[]) => void;
}

/**
 * Choose which of a Genie's transfer and escalation numbers appear on its
 * page. Those lines ring a person, so the page never shows one on its own —
 * the owner switches each on and says what visitors should call it.
 */
export const ContactPhonesPicker = ({
  genieId,
  primaryNumber,
  options,
  value,
  onChange,
}: ContactPhonesPickerProps) => {
  const toggle = (id: string, checked: boolean) =>
    onChange(checked ? [...value, { id, label: "" }] : value.filter((phone) => phone.id !== id));

  const relabel = (id: string, label: string) =>
    onChange(value.map((phone) => (phone.id === id ? { ...phone, label } : phone)));

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-gray-700">Phone numbers</span>
      <p className="mb-2 text-[13px] leading-snug text-gray-600">
        {primaryNumber
          ? `Your Genie's own number, ${primaryNumber}, appears on the page automatically.`
          : "Your Genie's own number appears on the page automatically once it has one."}{" "}
        Transfer and escalation numbers reach your team directly, so they stay off the page until
        you add them here.
      </p>

      {options.length > 0 ? (
        <ul className="space-y-2">
          {options.map(({ phone, kind }) => {
            const id = String(phone.id);
            const picked = value.find((entry) => entry.id === id);
            return (
              <li key={id} className="rounded-xl border border-gray-200 bg-white px-3 py-2.5">
                <div className="flex min-h-[44px] items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{phone.number}</p>
                    <p className="truncate text-[12.5px] text-gray-500">
                      {[kind, phone.friendly_name].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <Switch
                    checked={Boolean(picked)}
                    onCheckedChange={(checked) => toggle(id, checked)}
                    aria-label={`Show ${phone.number} on the page`}
                  />
                </div>
                {picked && (
                  <div className="mt-2">
                    <input
                      value={picked.label ?? ""}
                      onChange={(event) => relabel(id, event.target.value.slice(0, MAX_LABEL))}
                      placeholder="What visitors see, e.g. Service technicians"
                      aria-label={`Label for ${phone.number}`}
                      className={inputClass}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave blank to show it as “Call us”.
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
          This Genie has no transfer or escalation numbers yet.{" "}
          <Link
            to={`/genie-hub/my-genies/${genieId}`}
            className="font-medium text-genie-primary underline-offset-2 hover:underline"
          >
            Add one to the Genie
          </Link>{" "}
          and it will be offered here.
        </p>
      )}
    </div>
  );
};

export default ContactPhonesPicker;
